// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Container,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { RetryAlert, SaveAlerts } from '../../Common/ErrorMessages';
import { ApiError } from '../../../utils/serverErrors';
import { PricingModelSummary } from '../PricingModels/pricingModels';
import { SCOPING_MODELS_PATH, ScopingModelSummary } from '../ScopingModels/scopingModels';
import { Loaded, useLoaded } from '../ScopingModels/useLoaded';
import { DatasetMetadataField } from './DatasetMetadataFields';
import * as goldenDatasets from './goldenDatasets';

interface ModelPickerProps<T> {
  id: 'pricingModelId' | 'scopingModelId';
  label: string;
  models: Loaded<T[]>;
  idOf: (model: T) => string;
  nameOf: (model: T) => string;
  selectedId: string;
  errorText?: string;
  disabled: boolean;
  onChange: (id: string) => void;
}

/**
 * A required model picker offering every model, each marked Live or Pending, with a retry when they
 * fail to load.
 */
function ModelPicker<T extends { version: number | null }>({
  id,
  label,
  models,
  idOf,
  nameOf,
  selectedId,
  errorText,
  disabled,
  onChange,
}: ModelPickerProps<T>): JSX.Element {
  const options: SelectProps.Option[] = (models.value || []).map(model => ({
    label: nameOf(model),
    value: idOf(model),
    labelTag: goldenDatasets.modelStateLabel(model.version !== null),
  }));
  const selected = options.find(option => option.value === selectedId) || null;
  return (
    <SpaceBetween size="xs">
      {models.errors.length > 0 && (
        <RetryAlert
          id={`${id}LoadError`}
          header={`Could not load the ${label.toLowerCase()}s; creating is unavailable`}
          messages={models.errors}
          retryId={`${id}Retry`}
          onRetry={models.reload}
        />
      )}
      <FormField
        id={`${id}-field`}
        label={label}
        description="A Pending model has no live version yet; cost impacts price it only from a change set that writes it."
        constraintText="Required. Cannot be changed later."
        errorText={errorText}
      >
        <Select
          id={id}
          options={options}
          selectedOption={selected}
          placeholder={`Choose a ${label.toLowerCase()}`}
          filteringType="auto"
          statusType={models.value || models.errors.length > 0 ? 'finished' : 'loading'}
          loadingText={`Loading ${label.toLowerCase()}s`}
          empty={`No ${label.toLowerCase()}s`}
          invalid={Boolean(errorText)}
          disabled={disabled}
          onChange={({ detail }): void => onChange(detail.selectedOption.value as string)}
        />
      </FormField>
    </SpaceBetween>
  );
}

interface GoldenDatasetCreateProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPost: (path: string, body: object) => Promise<unknown>;
  };
  navigate: (path: string) => void;
  canWrite: boolean;
}

/** Creates a dataset with no rows; its rows are uploaded on its page. */
export const GoldenDatasetCreate = ({
  apiClient,
  navigate,
  canWrite,
}: GoldenDatasetCreateProps): JSX.Element | null => {
  const [metadata, setMetadata] = useState<goldenDatasets.Metadata>({ name: '', description: '' });
  const [pricingModelId, setPricingModelId] = useState('');
  const [scopingModelId, setScopingModelId] = useState('');
  const [failure, setFailure] = useState<goldenDatasets.WriteFailure>(goldenDatasets.NO_FAILURE);
  const [submitting, setSubmitting] = useState(false);
  const pricingModels = useLoaded<PricingModelSummary[]>(
    apiClient,
    canWrite ? '/pricingModels' : null,
    goldenDatasets.describePricingModelsError
  );
  const scopingModels = useLoaded<ScopingModelSummary[]>(
    apiClient,
    canWrite ? SCOPING_MODELS_PATH : null,
    goldenDatasets.describeScopingModelsError
  );
  // For the pair check, which is skipped if it fails to load; reloaded after a 409.
  const datasets = useLoaded<goldenDatasets.GoldenDatasetSummary[]>(
    apiClient,
    canWrite ? goldenDatasets.GOLDEN_DATASETS_PATH : null,
    goldenDatasets.describeError
  );
  const existing = datasets.value || [];

  const pairOwner = goldenDatasets.pairTakenBy(pricingModelId, scopingModelId, existing);
  const pairConflict = pairOwner && goldenDatasets.duplicatePairMessage(pairOwner);
  const ready =
    goldenDatasets.metadataValid(metadata) &&
    pricingModelId !== '' &&
    scopingModelId !== '' &&
    !pairConflict;

  const clearFieldError = (field: goldenDatasets.WriteField): void =>
    setFailure(current => ({
      ...current,
      fieldErrors: { ...current.fieldErrors, [field]: undefined },
    }));

  const create = (): void => {
    setSubmitting(true);
    setFailure(goldenDatasets.NO_FAILURE);
    apiClient
      .httpPost(
        goldenDatasets.GOLDEN_DATASETS_PATH,
        goldenDatasets.toCreateRequest(metadata, pricingModelId, scopingModelId)
      )
      .then(
        response =>
          navigate(
            goldenDatasets.datasetRoute((response as goldenDatasets.GoldenDataset).datasetId)
          ),
        (error: ApiError) => {
          setSubmitting(false);
          setFailure(goldenDatasets.describeWriteFailure(error));
        }
      );
  };

  if (!canWrite) {
    return null;
  }

  const pickerFailed = pricingModels.errors.length > 0 || scopingModels.errors.length > 0;

  return (
    <SpaceBetween size="m">
      <SaveAlerts
        idPrefix="goldenDatasetCreate"
        conflictHeader={goldenDatasets.CONFLICT_HEADER}
        conflict={failure.conflict}
        formErrors={failure.formErrors}
        onReload={(): void => {
          setFailure(goldenDatasets.NO_FAILURE);
          datasets.reload();
        }}
      />
      <Container
        header={
          <Header
            variant="h2"
            description="Name the dataset and choose its models. Upload its rows on the dataset's page."
          >
            Create golden dataset
          </Header>
        }
      >
        <SpaceBetween size="m">
          <DatasetMetadataField
            field="name"
            label="Name"
            values={metadata}
            serverError={failure.fieldErrors.name}
            disabled={submitting}
            onChange={(values): void => {
              setMetadata(values);
              clearFieldError('name');
            }}
          />
          <DatasetMetadataField
            field="description"
            label="Description"
            values={metadata}
            serverError={failure.fieldErrors.description}
            disabled={submitting}
            onChange={(values): void => {
              setMetadata(values);
              clearFieldError('description');
            }}
          />
          <ModelPicker
            id="pricingModelId"
            label="Pricing model"
            models={pricingModels}
            idOf={(model): string => model.priceSchemeId}
            nameOf={(model): string => model.name}
            selectedId={pricingModelId}
            errorText={failure.fieldErrors.pricingModelId}
            disabled={submitting}
            onChange={(id): void => {
              setPricingModelId(id);
              clearFieldError('pricingModelId');
            }}
          />
          <ModelPicker
            id="scopingModelId"
            label="Scoping model"
            models={scopingModels}
            idOf={(model): string => model.weightSchemeId}
            nameOf={(model): string => model.name}
            selectedId={scopingModelId}
            errorText={failure.fieldErrors.scopingModelId || pairConflict}
            disabled={submitting}
            onChange={(id): void => {
              setScopingModelId(id);
              clearFieldError('scopingModelId');
            }}
          />
          <Button
            id="createGoldenDataset"
            variant="primary"
            loading={submitting}
            disabled={submitting || pickerFailed || !ready}
            onClick={create}
          >
            Create
          </Button>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

GoldenDatasetCreate.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
};

const GoldenDatasetCreateView = (
  props: Omit<GoldenDatasetCreateProps, 'apiClient'>
): JSX.Element | null => <GoldenDatasetCreate apiClient={ApiClient} {...props} />;

export default GoldenDatasetCreateView;
